import { isEmpty } from 'lodash'
import Cookies from 'js-cookie'

import { isPublicPath } from '../history'
import { apiURL } from '../env'

export const OFFLINE_NETWORK_CHECKING_DEFER = 2000 // 2s
const MAX_OFFLINE_NETWORK_WAIT_TIME = 120_000 // 120s

/**
 * HTTP response status codes enum.
 */
export const StatusCode = {
    OK: 200,
    UNAUTHORIZED: 401,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
    CONTENT_TOO_LARGE: 413
}

/**
 * Base class for handling requests to the back end API.
 *
 * Specific requests must be added to dedicated interfaces.
 * All interfaces must not extend this one, but instead invoke
 * the instance of this class available at **window.Service**.
 *
 * If you need to make a request in the initial loading stages of the app,
 * you can check if the instance is ready to be invoked using **app.coreApiState**
 * in the Redux state of the application. States may vary between `load` and `ready`.
 */
export default class API {
    static staticIdentityAccessToken = null

    constructor(args = {}) {
        const {
            onIdentityAccessTokenExpired,
            getIdentityAccessToken,
            signOutCallback,
            onNetworkError,
            getAuthToken,
            application
        } = args || {}
        this.application = application || 'APP'
        this.onNetworkError = onNetworkError && typeof onNetworkError === 'function' ? onNetworkError : () => undefined
        this.signOutCallback = signOutCallback && typeof signOutCallback === 'function' ? signOutCallback : () => undefined
        this.getAuthToken = getAuthToken && typeof getAuthToken === 'function' ? getAuthToken : () => {}
        this.getIdentityAccessToken = getIdentityAccessToken && typeof getIdentityAccessToken === 'function'
            ? getIdentityAccessToken : () => {}
        this.onIdentityAccessTokenExpired = onIdentityAccessTokenExpired && typeof onIdentityAccessTokenExpired === 'function'
            ? onIdentityAccessTokenExpired : () => undefined
    }

    // eslint-disable-next-line class-methods-use-this
    getURL(path, method, payload) {
        const url = `${apiURL}/${path}`
        if (method === 'GET' && !isEmpty(payload)) {
            const params = Object.entries(payload)
                .map(([key, val]) => `${key}=${val}`).join('&')
            return url.concat('?', params)
        }
        return url
    }

    getHeaders(auth) {
        const headers = {
            'Content-Type': 'application/json',
            'Request-Source': this.application,
            'X-Client-Version': process.env.REACT_APP_VERSION_NUMBER
        }

        if (auth) {
            const token = this.getAuthToken()
            if (token) headers.Authorization = `Bearer ${this.getAuthToken()}`
        }

        try {
            const csrfToken = localStorage.getItem('crfsToken')
            if (csrfToken) headers['X-CSRFToken'] = csrfToken
        } catch (e) {
            console.error(e)
        }

        const identityAccessToken = this.getIdentityAccessToken() || API.staticIdentityAccessToken
        if (identityAccessToken) headers['X-Identity-Access-Authorization'] = identityAccessToken

        const canary = Cookies.get('canary')
        if (canary) {
            headers.canary = canary
        }
        return { headers }
    }

    // eslint-disable-next-line class-methods-use-this
    getSettings(method) {
        return {
            cache: 'no-cache',
            credentials: 'include',
            method
        }
    }

    // eslint-disable-next-line class-methods-use-this
    getBody(method, payload) {
        return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method) && payload
            ? { body: JSON.stringify(payload) }
            : null
    }

    getRequestData(method, auth, payload, signal, keepAlive) {
        return {
            ...this.getSettings(method),
            ...this.getHeaders(auth),
            ...this.getBody(method, payload),
            ...signal && signal instanceof AbortSignal ? { signal } : undefined,
            ...keepAlive ? { keepAlive } : {}
        }
    }

    /**
     * Make a request to the Core API.
     *
     * @param {string} path - The API endpoint.
     * @param {any} payload - Request body.
     * @param {"GET"|"POST"|"PUT"|"PATCH"|"DELETE"} method - Request method.
     * @param {boolean} auth - If request should include user's authentication.
     * @param {boolean} download - If request is for a file download.
     * @param {AbortSignal} signal - An abort signal, allowing to cancel the request.
     * @param {boolean} keepAlive - If request should be kept alive.
     * @returns {Promise<{status: number, values: any, message: string}>}
     */
    request(
        path,
        payload = null,
        method = 'GET',
        auth = true,
        download = false,
        signal = null,
        keepAlive = false
    ) {
        return new Promise(resolve => {
            const makeRequest = () => fetch(
                this.getURL(path, method, payload),
                this.getRequestData(method, auth, payload, signal, keepAlive)
            )

            const onForward = (response) => resolve(response)

            const onSuccess = (response) => {
                if (response.status === 401) {
                    if (!isPublicPath(window.location.pathname)) {
                        this.signOutCallback()
                    } else if (path !== 'v2/user') {
                        this.onIdentityAccessTokenExpired()
                    }
                    return { status: 401 }
                }
                if (response.status === 403) {
                    // T759 — this used to discard the entire response body on ANY
                    // 403, app-wide, including a structured `error_payload` a
                    // backend refusal may carry (e.g. billing/portal/redirect-token's
                    // documented `enterprise_portal_unavailable`, T574). The generic
                    // `message` below is kept as the default so callers that only
                    // read `.message` see byte-identical behaviour; `error_payload`
                    // is now forwarded when the body has one, so a caller that wants
                    // to branch on `.error_payload.code` finally can. A body that
                    // fails to parse (or carries no refusal) resolves to exactly
                    // the old shape.
                    //
                    // SG-14643 — billing/portal/redirect-token's refusal is NOT
                    // wrapped: core-api answers `jsonfy_with_status(403, {'code':
                    // 'enterprise_portal_unavailable'})`, the code at the TOP LEVEL
                    // of the body (billing/views.py, two branches). Forwarding only
                    // `body.error_payload` therefore never surfaced it and every
                    // Enterprise account kept seeing the generic portal error —
                    // caught by the 16 Sep local walk, not by the unit tests, which
                    // had mocked the wire shape. A top-level string `code` is now
                    // normalised into `error_payload: { code }`; a real
                    // `error_payload` still wins untouched.
                    return response.json()
                        .then((body) => {
                            const generic = {
                                status: 403,
                                message: `You don't have permission to do this action.`
                            }
                            if (!body || typeof body !== 'object') return generic
                            if (body.error_payload) return { ...generic, error_payload: body.error_payload }
                            if (typeof body.code === 'string' && body.code) {
                                return { ...generic, error_payload: { code: body.code } }
                            }
                            return generic
                        })
                        .catch(() => ({
                            status: 403,
                            message: `You don't have permission to do this action.`
                        }))
                }
                if (response.status === 421) return { status: 421 }
                if (download) {
                    if (response.status === 200) {
                        return response.blob()
                    }
                    if ([StatusCode.CONTENT_TOO_LARGE].includes(response.status)) {
                        return { status: response.status }
                    }
                    return false
                }
                return response.json()
            }

            const onError = async (error, offlineHoldBack = true) => {
                const response = { status: 0 }
                if (error) {
                    if (error.statusText === 'timeout') {
                        response.status = 12
                        response.message = 'Connection timed out'
                    } else if (
                        offlineHoldBack && window.navigator &&
                        window.navigator.onLine === false
                    ) {
                        // Inform user that they are offline
                        this.onNetworkError()
                        // Await to see if network connects again
                        let awaitCount = 0
                        // eslint-disable-next-line no-loops/no-loops
                        while (!navigator.onLine && awaitCount < MAX_OFFLINE_NETWORK_WAIT_TIME) {
                            // User is still offline, awaiting...
                            // eslint-disable-next-line no-await-in-loop
                            await new Promise(resolve => {
                                setTimeout(resolve, OFFLINE_NETWORK_CHECKING_DEFER)
                            })
                            awaitCount += OFFLINE_NETWORK_CHECKING_DEFER
                        }
                        if (navigator.onLine) {
                            // Try the request one last time
                            makeRequest()
                                .then(onSuccess)
                                .then(onForward)
                                .catch((e) => onError(e, false))
                            return
                        }
                    } else {
                        const text = (error.statusText || 'Unknown error')
                        response.message = text.charAt(0).toUpperCase() + text.slice(1)
                    }
                }
                resolve(response)
            }

            makeRequest()
                .then(onSuccess)
                .then(onForward)
                .catch(onError)
        })
    }

    /**
     * Make a GET request to the Core API.
     *
     * @param {string} path - The API endpoint.
     * @param {object} options - Request options.
     * @param {boolean} options.auth - If request should include user's authentication.
     * @param {boolean} options.download - If request is for a file download.
     * @param {AbortSignal} options.signal - An abort signal, allowing to cancel the request.
     * @param {boolean} options.keepAlive - If request should be kept alive.
     * @returns {Promise<{status: number, values: any, message: string}>}
     */
    get(path, options = null) {
        const { auth = true, download = false, signal = null, keepAlive = false } = options || {}
        return this.request(path, null, 'GET', auth, download, signal, keepAlive)
    }

    /**
     * Make a POST request to the Core API.
     *
     * @param {string} path - The API endpoint.
     * @param {any} body - Request body/data.
     * @param {object} options - Request options.
     * @param {boolean} options.auth - If request should include user's authentication.
     * @param {boolean} options.download - If request is for a file download.
     * @param {AbortSignal} options.signal - An abort signal, allowing to cancel the request.
     * @param {boolean} options.keepAlive - If request should be kept alive.
     * @returns {Promise<{status: number, values: any, message: string}>}
     */
    post(path, body = null, options = null) {
        const { auth = true, download = false, signal = null, keepAlive = false } = options || {}
        return this.request(path, body, 'POST', auth, download, signal, keepAlive)
    }

    /**
     * Make a DELETE request to the Core API.
     *
     * @param {string} path - The API endpoint.
     * @param {object} options - Request options.
     * @param {boolean} options.auth - If request should include user's authentication.
     * @param {boolean} options.download - If request is for a file download.
     * @param {AbortSignal} options.signal - An abort signal, allowing to cancel the request.
     * @param {boolean} options.keepAlive - If request should be kept alive.
     * @returns {Promise<{status: number, values: any, message: string}>}
     */
    delete(path, options = null) {
        const { auth = true, download = false, signal = null, keepAlive = false } = options || {}
        return this.request(path, null, 'DELETE', auth, download, signal, keepAlive)
    }
}
